/**
 * 视频上传和处理组件
 * 
 * 功能：
 * - 上传视频文件
 * - 选择目标语言和源语言
 * - 触发后端处理
 * - 显示处理进度和结果
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './VideoUpload.css';
import './VideoEdit.css';
import { HelpCircle, Play, CheckCircle, Clock, RefreshCw, AlertTriangle, XCircle } from 'lucide-react';
import AppShell from './AppShell';
import { ConfirmDialog } from './ConfirmDialog';
import HelpDialog from './HelpDialog';
import { Select, SelectItem } from './Select';
import { Input } from './Input';
import { API_BASE_URL } from '../config/api';
import { resolveUploadUrl } from '../utils/urls';
import { getAuthHeaders, clearAuth, getUser, getToken, type UserRole } from '../utils/auth';
import type { ApiResponse, UploadResponseData, StatusResponseData, VideoDescriptor } from '../types/api';
import type { VideoState } from '../types/status';

const VideoUpload: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileMeta, setSelectedFileMeta] = useState<string>(''); // 文件大小和时长显示
  const [targetLang, setTargetLang] = useState<string>('zh');
  const [sourceLang, setSourceLang] = useState<string>('');
  const [enableWordAlignment, setEnableWordAlignment] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [showLanguage, setShowLanguage] = useState<boolean>(true);
  const [showStatusSection, setShowStatusSection] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('未开始');
  const [statusType, setStatusType] = useState<'info' | 'success' | 'error' | 'processing'>('info');
  const [progress, setProgress] = useState<number>(0);
  const [progressText, setProgressText] = useState<string>('');
  const [showProgress, setShowProgress] = useState<boolean>(false);
  const [completedVideoId, setCompletedVideoId] = useState<string | null>(null);
  const [completedDescriptor, setCompletedDescriptor] = useState<VideoDescriptor | null>(null);
  const [userRole, setUserRole] = useState<UserRole>('user');
  const [visibility, setVisibility] = useState<'private' | 'public'>('private');
  const [videoName, setVideoName] = useState<string>(''); // 视频显示名称
  const [remainingUploads, setRemainingUploads] = useState<number>(0); // 可上传剩余数
  const [lastUserInfoUpdate, setLastUserInfoUpdate] = useState<number>(0); // 最后更新用户信息的时间戳
  const [isRefreshingUserInfo, setIsRefreshingUserInfo] = useState<boolean>(false); // 是否正在刷新用户信息
  const [showHelpDialog, setShowHelpDialog] = useState<boolean>(false);
  const [showVideoNameHelpDialog, setShowVideoNameHelpDialog] = useState<boolean>(false);
  const [showWordAlignHelpDialog, setShowWordAlignHelpDialog] = useState<boolean>(false);
  const [showVisibilityHelpDialog, setShowVisibilityHelpDialog] = useState<boolean>(false);
  const [topNotice, setTopNotice] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const statusEventSourceRef = useRef<EventSource | null>(null);
  const hasFetchedDescriptorRef = useRef<boolean>(false);
  const userInfoIntervalRef = useRef<NodeJS.Timeout | null>(null); // 定期刷新用户信息定时器
  const hasInitializedUserInfoRef = useRef<boolean>(false); // 标记是否已经初始化过用户信息
  const navigate = useNavigate();
  const location = useLocation();
  const blurActiveInput = () => {
    const el = document.activeElement as HTMLElement | null;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
      try {
        el.blur();
      } catch (e) {
        // ignore
      }
    }
  };

  // header was previously rendered by AppShell; Upload now renders content only.

  // 刷新用户信息函数
  const refreshUserInfo = async (forceUpdate: boolean = false) => {
    const user = getUser();
    if (!user) {
      console.log('VideoUpload - 未找到用户信息，跳过刷新');
      return;
    }

    console.log('VideoUpload - 开始刷新用户信息:', {
      userId: user.id,
      username: user.username,
      currentRole: userRole,
      currentRemainingUploads: remainingUploads,
      lastUpdate: new Date(lastUserInfoUpdate).toISOString(),
      forceUpdate
    });

    // 如果不是强制更新且距离上次更新不到5秒，则跳过
    if (!forceUpdate && Date.now() - lastUserInfoUpdate < 5000) {
      console.log('VideoUpload - 距离上次更新不足5秒，跳过刷新');
      return;
    }

    setIsRefreshingUserInfo(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data: ApiResponse<{ id: string; username: string; email: string | null; role: string; remainingUploads?: number; hasPassword?: boolean }> = await response.json();
        console.log('VideoUpload - 收到服务器响应:', {
          success: data.success,
          data: data.data,
          forceUpdate,
          timestamp: new Date().toISOString()
        });

        if (data.success && data.data) {
          // 更新 localStorage 中的用户信息
          const token = localStorage.getItem('auth_token');
          if (token) {
            const updatedUser = { ...user, ...data.data };
            localStorage.setItem('user_info', JSON.stringify(updatedUser));
            const role = data.data.role as UserRole;
            const newRemainingUploads = data.data.remainingUploads ?? 0;

            console.log('VideoUpload - 更新前状态:', {
              oldRole: userRole,
              oldRemainingUploads: remainingUploads,
              newRole: role,
              newRemainingUploads: newRemainingUploads
            });

            setUserRole(role);
            setRemainingUploads(newRemainingUploads);
            setLastUserInfoUpdate(Date.now());

            // 根据剩余上传次数设置status
            if (newRemainingUploads === 0) {
              setStatus('剩余上传次数为0，无法上传新视频');
              setStatusType('error');
            } else {
              // 如果之前是错误状态且是因为剩余次数为0，现在恢复正常状态
              setStatus('未开始');
              setStatusType('info');
            }

            // 高级用户只能选择 private
            if (role === 'premium') {
              setVisibility('private');
            }

            // 非admin用户不能使用词级对齐
            if (role !== 'admin') {
              setEnableWordAlignment(false);
            }

            console.log('VideoUpload - 用户信息已刷新并更新状态:', {
              role,
              remainingUploads: newRemainingUploads,
              forceUpdate,
              timestamp: new Date().toISOString()
            });
          }
        } else {
          console.warn('VideoUpload - 服务器返回失败或无数据:', data);
        }
      }
    } catch (err) {
      console.error('VideoUpload - 刷新用户信息失败:', {
        error: err,
        userId: user.id,
        username: user.username,
        forceUpdate,
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsRefreshingUserInfo(false);
    }
  };

  useEffect(() => {
    // 获取用户信息，确定用户角色和可上传剩余数
    const user = getUser();
    if (user) {
      // 确保用户信息初始化只执行一次（避免StrictMode下的重复执行）
      if (!hasInitializedUserInfoRef.current) {
        hasInitializedUserInfoRef.current = true;

        // 设置本地已有的信息
        setUserRole(user.role || 'user');
        const currentRemainingUploads = user.remainingUploads ?? 0;
        setRemainingUploads(currentRemainingUploads);

        // 如果剩余上传次数为0，在status中显示提示
        if (currentRemainingUploads === 0) {
          setStatus('剩余上传次数为0，无法上传新视频');
          setStatusType('error');
        }

        // 高级用户只能选择 private
        if (user.role === 'premium') {
          setVisibility('private');
        }

        // 非admin用户不能使用词级对齐
        if (user.role !== 'admin') {
          setEnableWordAlignment(false);
        }

        // 立即刷新一次用户信息
        refreshUserInfo(true);
      }

      // 监听页面可见性变化（用户切换标签页时立即刷新）
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          console.log('VideoUpload - 页面重新获得焦点，刷新用户信息');
          refreshUserInfo(true);
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // 清理函数
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        hasInitializedUserInfoRef.current = false; // 组件卸载时重置标记
      };
    } else {
      console.log('VideoUpload - 未获取到用户信息');
    }
  }, []);


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 读取视频元数据（用于显示时长信息，但不阻止上传）
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration; // 秒
        // 设置文件（不进行时长校验，允许用户上传）
      const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      let autoFilledName = false;
      if (!videoName.trim()) {
        setVideoName(fileNameWithoutExt);
        autoFilledName = true;
      }
      setSelectedFile(file);
      setSelectedFileMeta(`${formatFileSize(file.size)} · ${formatDuration(duration)}`);
      // 选择文件后状态仍为未开始
      setStatus('未开始');
      setStatusType('info');
      // 如果自动填充了名称或已有名称，则清除顶部提示；如果名称为空（用户手动清空），则提示填写
      if (autoFilledName || fileNameWithoutExt.trim()) {
        setTopNotice(null);
      } else if (!videoName.trim()) {
        setTopNotice('请填写视频名称');
      } else {
        setTopNotice(null);
      }
        // 选择新文件时重置所有处理状态
        setCompletedVideoId(null);
        setCompletedDescriptor(null);
        setShowProgress(false);
        setProgress(0);
        setProgressText('');
        hasFetchedDescriptorRef.current = false; // 重置标志
      };
      
      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        // 如果无法读取视频元数据，仍然允许上传（后端会进行校验）
        setSelectedFile(file);
        setSelectedFileMeta(`${formatFileSize(file.size)}`);
      const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      if (!videoName.trim()) {
        setVideoName(fileNameWithoutExt);
        setTopNotice(null);
      }
      // 选择文件后状态仍为未开始
      setStatus('未开始');
      setStatusType('info');
      // 选择新文件时重置所有处理状态
      setCompletedVideoId(null);
      setCompletedDescriptor(null);
      setShowProgress(false);
      setProgress(0);
      setProgressText('');
        hasFetchedDescriptorRef.current = false;
      };
      
      video.src = URL.createObjectURL(file);
    }
  };

  const handleProcess = async () => {
    if (!selectedFile) {
      setStatus('请先选择视频文件');
      setStatusType('error');
      return;
    }
    // 首先校验用户存在与权限，若不满足则在顶部提示并中止
    const user = getUser();
    if (!user) {
      setTopNotice('用户不存在，请先登录或刷新页面');
      setStatus('请先登录');
      setStatusType('error');
      return;
    }
    if (!(user.role === 'premium' || user.role === 'super' || user.role === 'admin')) {
      setTopNotice('权限不足，无法上传视频');
      setStatus('权限不足');
      setStatusType('error');
      return;
    }
    // 校验视频名称
    if (!videoName.trim()) {
      setTopNotice('请填写视频名称');
      setStatus('请填写视频名称');
      setStatusType('error');
      return;
    }
    // 检查可上传剩余数
    if (remainingUploads <= 0) {
      setStatus('可上传剩余数为0，无法上传新视频');
      setStatusType('error');
      return;
    }

    // 乐观更新：立即减少前端显示的剩余上传数（premium 用户）
    const originalRemainingUploads = remainingUploads;
    if (userRole === 'premium' && remainingUploads > 0) {
      setRemainingUploads(remainingUploads - 1);
      console.log('VideoUpload - 乐观更新：剩余上传数减少1');
    }

    // collapse other sections and open status, then show progress
    setShowLanguage(false);
    setShowAdvanced(false);
    setShowStatusSection(true);
    setShowProgress(true);
    setProgress(0);
    setProgressText('准备上传...');
    setStatus('正在上传文件...');
    setStatusType('processing');
    setCompletedVideoId(null);

    try {
      // 1. 上传文件
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('targetLang', targetLang);
      if (sourceLang.trim()) {
        formData.append('sourceLang', sourceLang.trim());
      }
      // 添加可见性参数（只有高级用户和管理者可以上传，所以这里一定有值）
      if (userRole !== 'user') {
        formData.append('visibility', visibility);
      }
      // 添加视频名称（如果用户输入了）
      if (videoName.trim()) {
        formData.append('name', videoName.trim());
      }

      // 上传文件时，不要设置 Content-Type，让浏览器自动设置（包含 boundary）
      const headers = new Headers();
      const token = getToken();
      if (token) {
        headers.append('Authorization', `Bearer ${token}`);
      }
      // 注意：不要设置 Content-Type，FormData 会自动设置 multipart/form-data 和 boundary

      const uploadResponse = await fetch(resolveUploadUrl('/upload'), {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (!uploadResponse.ok) {
        if (uploadResponse.status === 401) {
          clearAuth();
          navigate('/login');
          return;
        }
        const contentType = uploadResponse.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          // 尝试解析 JSON 错误信息
          try {
            const errorData: ApiResponse = await uploadResponse.json();
            // 检查是否是时长超限错误，显示友好提示（不显示具体数字）
            if (errorData.errorCode === 'VIDEO_DURATION_EXCEEDED') {
              throw new Error('该视频时长较长，当前暂时无法完成处理');
            }
            if (errorData.error) {
              throw new Error(errorData.error);
            }
          } catch (parseError) {
            if (parseError instanceof Error) {
              throw parseError;
            }
          }
        } else {
          // 非 JSON 响应（如 Cloudflare 错误页），仅输出调试信息到控制台
          try {
            const rawText = await uploadResponse.text();
            // 截断到前 200 字符，避免日志过长
            console.error(
              'VideoUpload - 非 JSON 上传错误响应（前 200 字符）:',
              rawText.slice(0, 200)
            );
          } catch {
            // ignore logging errors
          }
        }
        throw new Error('文件上传失败');
      }

      const uploadResponseData: ApiResponse<UploadResponseData> = await uploadResponse.json();
      if (!uploadResponseData.success || !uploadResponseData.data) {
        // 检查是否是时长超限错误，显示友好提示（不显示具体数字）
        if (uploadResponseData.errorCode === 'VIDEO_DURATION_EXCEEDED') {
          throw new Error('该视频时长较长，当前暂时无法完成处理');
        }
        throw new Error(uploadResponseData.error || '文件上传失败');
      }
      const uploadData = uploadResponseData.data;
      setProgress(20);
      setProgressText('文件上传成功，开始处理...');
      setStatus('视频上传成功，正在后台处理...');
      setStatusType('processing');

      // 上传成功后直接使用 upload 接口返回的 remainingUploads 更新前端状态（避免额外请求 /auth/me）
      const user = getUser();
      if (user && user.role === 'premium') {
        const newRemaining = uploadResponseData.data?.remainingUploads ?? remainingUploads;
        try {
          const token = localStorage.getItem('auth_token');
          const updatedUser = { ...user, remainingUploads: newRemaining };
          if (token) {
            localStorage.setItem('user_info', JSON.stringify(updatedUser));
          }
          // role 保持不变（后端未修改），只更新前端展示值
          setRemainingUploads(newRemaining);
          console.log('VideoUpload - 上传成功后已更新用户信息（来自 upload 响应）:', { remainingUploads: newRemaining });
        } catch (err) {
          console.warn('VideoUpload - 更新本地用户信息失败:', err);
        }
      }

      // 2. 开始处理
      // 确保只有admin用户才能启用词级对齐
      const finalEnableWordAlignment = userRole === 'admin' ? enableWordAlignment : false;
      const processResponse = await fetch(`${API_BASE_URL}/process`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          videoId: uploadData.videoId,
          targetLang: uploadData.targetLang,
          sourceLang: uploadData.sourceLang,
          enableWordAlignment: finalEnableWordAlignment,
        }),
      });

      if (!processResponse.ok) {
        throw new Error('处理请求失败');
      }

      await processResponse.json();
      setProgress(50);
      setProgressText('视频处理已开始，请等待...');

      // 3. 开始状态订阅（优先使用 SSE，fallback 到 HTTP 轮询）
      // 只使用 API 返回的 videoId，不进行任何推断
      if (!uploadData.videoId) {
        throw new Error('无法获取视频 ID');
      }
      startStatusStream(uploadData.videoId);

    } catch (error) {
      setStatus(`错误: ${error instanceof Error ? error.message : String(error)}`);
      setStatusType('error');
      setShowProgress(false);

      // 回滚乐观更新：如果上传失败，恢复原来的剩余上传数
      if (userRole === 'premium') {
        setRemainingUploads(originalRemainingUploads);
        console.log('VideoUpload - 回滚乐观更新：恢复剩余上传数', originalRemainingUploads);
      }
    }
  };

  /**
   * 获取完成后的 VideoDescriptor（纯展示用，不参与业务判断）
   */
  const fetchDescriptorAfterCompleted = async (videoId: string) => {
    try {
      const resp = await fetch(`${API_BASE_URL}/video/metadata?videoId=${encodeURIComponent(videoId)}`, {
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        throw new Error(`HTTP error! status: ${resp.status}`);
      }
      const json: ApiResponse<VideoDescriptor> = await resp.json();
      if (!json.success || !json.data) {
        throw new Error(json.error || '获取视频详情失败');
      }
      setCompletedDescriptor(json.data);
    } catch (e) {
      console.warn('加载完成后 VideoDescriptor 失败: ', e);
      setCompletedDescriptor(null);
    }
  };

  /**
   * 统一的状态更新处理函数
   * 根据 StatusResponseData 更新 UI 状态
   */
  const updateStatusFromResponse = (statusData: StatusResponseData) => {
    const { state, progress, message, error } = statusData;

    // 【边界 2】：根据 state 设置 statusType
    if (state === 'completed') {
      // 进度条强制设为 100（如果后端没给 100，也要在前端补一下）
      setProgress(100);
      setProgressText('处理完成！');
      setStatus(message || '处理完成！');
      setStatusType('success');
      setShowProgress(false);
      setCompletedVideoId(statusData.videoId);
      
      // 只在第一次进入 completed 时调用一次，避免重复请求
      if (!hasFetchedDescriptorRef.current) {
        hasFetchedDescriptorRef.current = true;
        fetchDescriptorAfterCompleted(statusData.videoId);

        // 处理完成后无需额外调用 /auth/me；如果需要可在 fetchDescriptorAfterCompleted 中同步数据或依赖 upload 响应已更新的本地信息
      }

      return true; // 返回 true 表示已完成
    } else if (state === 'error') {
      // 从 error 字段取错误消息
      let errorMsg = error || '处理失败';
      // 检查是否是时长超限错误，显示友好提示（不显示具体数字）
      if (typeof errorMsg === 'string' && (errorMsg.includes('10 分钟') || errorMsg.includes('VIDEO_DURATION_EXCEEDED'))) {
        errorMsg = '该视频时长较长，当前暂时无法完成处理';
      }
      setProgress(Math.max(progress, 0)); // 保持失败时的进度值
      setProgressText('处理失败');
      setStatus(errorMsg);
      setStatusType('error');
      setShowProgress(false);
      return true; // 返回 true 表示已完成（失败）
    } else {
      // 其他状态（processing / extracting_audio / transcribing 等）
      // 根据 message 更新前端的 status 文本
      setStatus(message || '正在处理视频...');
      setStatusType('processing');
      // 根据 progress 更新进度条（0-100，适当 clamp）
      const clampedProgress = Math.max(0, Math.min(100, progress));
      setProgress(clampedProgress);
      setProgressText(message || `处理中... (${clampedProgress}%)`);
      return false; // 返回 false 表示仍在处理中
    }
  };

  /**
   * 启动状态流订阅（优先使用 SSE，fallback 到 HTTP 轮询）
   */
  const startStatusStream = (videoId: string) => {
    // 先清理可能存在的旧连接
    if (statusEventSourceRef.current) {
      statusEventSourceRef.current.close();
      statusEventSourceRef.current = null;
    }

    // 检查浏览器是否支持 EventSource
    if (typeof EventSource !== 'undefined') {
      try {
        // 获取 token 并添加到查询参数（EventSource 不支持自定义请求头）
        const token = getToken();
        const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
        
        // 优先使用 SSE
        const eventSource = new EventSource(
          `${API_BASE_URL}/status-stream?videoId=${encodeURIComponent(videoId)}${tokenParam}`
        );
        statusEventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          console.log('[SSE] 连接已建立');
        };

        eventSource.onmessage = (event) => {
          try {
            const statusData: StatusResponseData = JSON.parse(event.data);
            const isCompleted = updateStatusFromResponse(statusData);
            
            // 如果是终态，关闭 SSE 连接
            if (isCompleted) {
              eventSource.close();
              statusEventSourceRef.current = null;
            }
          } catch (error) {
            console.error('[SSE] 解析状态数据失败:', error);
            // SSE 解析失败，fallback 到 HTTP 轮询
            eventSource.close();
            statusEventSourceRef.current = null;
            checkProcessingStatus(videoId);
          }
        };

        eventSource.onerror = (error) => {
          console.error('[SSE] 连接错误:', error);
          // SSE 连接失败，fallback 到 HTTP 轮询
          eventSource.close();
          statusEventSourceRef.current = null;
          checkProcessingStatus(videoId);
        };
      } catch (error) {
        console.error('[SSE] 创建 EventSource 失败:', error);
        // EventSource 创建失败，fallback 到 HTTP 轮询
        checkProcessingStatus(videoId);
      }
    } else {
      // 浏览器不支持 EventSource，直接使用 HTTP 轮询
      console.log('[Fallback] 浏览器不支持 EventSource，使用 HTTP 轮询');
      checkProcessingStatus(videoId);
    }
  };

  /**
   * HTTP 轮询检查状态（作为 SSE 的 fallback）
   */
  const checkProcessingStatus = async (videoId: string) => {
    let attempts = 0;
    const maxAttempts = 300; // 最大尝试次数（15分钟，因为词级对齐可能需要较长时间）
    const checkIntervalTime = 3000;
    let checkInterval: NodeJS.Timeout | null = null;
    let isCompleted = false;

    const checkOnce = async (): Promise<boolean> => {
      if (isCompleted) {
        return true;
      }

      attempts++;

      try {
        const response = await fetch(`${API_BASE_URL}/check-status?videoId=${encodeURIComponent(videoId)}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const resp: ApiResponse<StatusResponseData> = await response.json();
        if (!resp.success || !resp.data) {
          throw new Error(resp.error || '检查状态失败');
        }
        const statusData: StatusResponseData = resp.data;
        const isDone = updateStatusFromResponse(statusData);

        if (isDone) {
          isCompleted = true;
          // 清除轮询
          if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
          }
          return true;
        }
      } catch (error) {
        // 状态检查失败，忽略错误（继续轮询）
      }

      // 兜底超时逻辑
      if (attempts >= maxAttempts) {
        isCompleted = true;
        setStatus('处理超时，请稍后在视频列表中确认处理结果。');
        setStatusType('error');
        setShowProgress(false);
        
        // 清除轮询
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
        return true;
      }

      return false;
    };

    // 立即检查一次
    const completed = await checkOnce();
    if (completed) {
      return;
    }

    // 然后每 3 秒检查一次
    checkInterval = setInterval(async () => {
      const completed = await checkOnce();
      if (completed && checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    }, checkIntervalTime);
  };

  // 组件卸载时清理 EventSource 连接
  useEffect(() => {
    return () => {
      if (statusEventSourceRef.current) {
        statusEventSourceRef.current.close();
        statusEventSourceRef.current = null;
      }
    };
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    if (!isFinite(seconds) || seconds <= 0) return '0:00';
    const total = Math.round(seconds);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // NOTE: 不再在组件顶层基于角色早退显示“功能受限”；
  // 如果用户没有权限或不存在，将在用户点击“开始处理”时进行检查并通过顶部提示展示信息。

  // 独立的"新建视频"页面顶栏：返回按钮 + 标题
  // 与 VideoEdit / CollectionEdit 的视觉风格保持一致（icon-only 返回 + 居中 h1）
  const header = (
    <div className="video-edit-header app-shell-header-content">
      <button
        type="button"
        onClick={() => navigate('/profile?tab=management')}
        className="back-button"
        title="返回我的视频"
        aria-label="返回我的视频"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>
      <h1>新建视频</h1>
    </div>
  );

  return (
    <AppShell header={header} footer={null}>
      {/* 顶部状态吸顶条（放为 app-shell-body 的直接子元素，紧贴顶部） */}
      <div className="video-upload-status-sticky">
        <div className="reminder-box" aria-hidden={!selectedFile}>
          <span className="reminder-title">提示</span>
          <div className="reminder-text">
            {topNotice
              ? topNotice
              : selectedFile
                ? '请选择语言和高级设置，然后按下「开始处理」按钮'
                : '请选择视频文件'}
          </div>
        </div>
      </div>

      {/* 主表单卡片（选择文件 + 语言设置 + 高级设置折叠） */}
      <div className="upload-card">
            <div className="upload-section">
              <div className="file-input-wrapper">
                <label
                  htmlFor="videoFile"
                  className={`file-input-label ${selectedFile ? 'has-file' : ''} ${remainingUploads === 0 ? 'disabled' : ''}`}
                  onClick={(e) => {
                    // prevent unexpected focus/behavior and use ref to open file dialog
                    e.preventDefault();
                    if (fileInputRef.current) fileInputRef.current.click();
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (fileInputRef.current) fileInputRef.current.click();
                    }
                  }}
                >
                  <svg className="file-input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                    <line x1="8" y1="21" x2="16" y2="21"></line>
                    <line x1="12" y1="17" x2="12" y2="21"></line>
                  </svg>
                  <span className="file-input-text">{selectedFile ? `已选择: ${selectedFile.name} (${selectedFileMeta})` : '选择视频文件'}</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="videoFile"
                  accept="video/*"
                  onChange={handleFileChange}
                  disabled={remainingUploads === 0}
                />
              </div>
            
            </div>

            <div className="settings-section">
              <button
                type="button"
                className="section-toggle"
                onClick={() => setShowLanguage(!showLanguage)}
                aria-expanded={showLanguage}
              >
                <span className="section-title">语言设置</span>
                <span className={`chevron ${showLanguage ? 'open' : ''}`} aria-hidden="true">▸</span>
              </button>

              <div
                className={`section-body ${showLanguage ? 'open' : ''}`}
                aria-hidden={!showLanguage}
              >
                  <div className="upload-form-grid">
                    <div className="upload-form-column">
                      <div className="form-group">
                        <label htmlFor="sourceLang">
                          视频语言
                          <HelpCircle
                            size={18}
                            strokeWidth={1.5}
                            className="help-icon help-icon--gap"
                            onMouseDown={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={() => { blurActiveInput(); setShowHelpDialog(true); }}
                            aria-label="帮助信息"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e: React.KeyboardEvent<SVGSVGElement>) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowHelpDialog(true);
                              }
                            }}
                          />
                        </label>
                        <Select
                          value={sourceLang || "auto"}
                          onValueChange={(value) => setSourceLang(value === "auto" ? "" : value)}
                          placeholder="自动检测"
                        >
                          <SelectItem value="auto">自动检测</SelectItem>
                          <SelectItem value="zh">中文（简体）</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="ja">日本語</SelectItem>
                          <SelectItem value="ko">한국어</SelectItem>
                          <SelectItem value="es">Español</SelectItem>
                          <SelectItem value="fr">Français</SelectItem>
                        </Select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="targetLang">翻译语言</label>
                        <Select
                          value={targetLang}
                          onValueChange={setTargetLang}
                        >
                          <SelectItem value="zh">中文（简体）</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="ja">日本語</SelectItem>
                          <SelectItem value="ko">한국어</SelectItem>
                          <SelectItem value="es">Español</SelectItem>
                          <SelectItem value="fr">Français</SelectItem>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              
            </div>

            <HelpDialog
              isOpen={showHelpDialog}
              title="帮助"
              message={'选择视频的原始语言，选择"自动检测"将由系统自动识别'}
              onClose={() => setShowHelpDialog(false)}
            />

            <HelpDialog
              isOpen={showVisibilityHelpDialog}
              title="帮助"
              message={
                <>
                  <p>公开视频：所有人可见，会显示在「首页」</p>
                  <p>私有视频：仅自己可见，可在「我的」→「我的视频」中查看和管理</p>
                </>
              }
              onClose={() => setShowVisibilityHelpDialog(false)}
            />

            <HelpDialog
              isOpen={showVideoNameHelpDialog}
              title="帮助"
              message={
                <>
                  <p>上传时可自定义的视频名称</p>
                  <p>可在「我的」→「我的视频」→「视频卡片」→「编辑」中修改</p>
                </>
              }
              onClose={() => setShowVideoNameHelpDialog(false)}
            />

            <HelpDialog
              isOpen={showWordAlignHelpDialog}
              title="帮助"
              message={
                <>
                  <p>如果启用，将对每个词进行精确时间戳对齐，用于前端逐词高亮显示</p>
                    <p className="form-warning-text" style={{ marginTop: '12px' }}>
                    <AlertTriangle size={14} className="icon-inline" aria-hidden="true" /> 注意：开启后会使用额外的声学模型进行强制对齐，处理时间将会显著延长
                  </p>
                </>
              }
              onClose={() => setShowWordAlignHelpDialog(false)}
            />

            {/* 高级设置折叠区域 */}
            <div className="settings-section">
              <button
                type="button"
                className="section-toggle"
                onClick={() => setShowAdvanced(!showAdvanced)}
                aria-expanded={showAdvanced}
              >
                <span className="section-title">高级设置</span>
                <span className={`chevron ${showAdvanced ? 'open' : ''}`} aria-hidden="true">▸</span>
              </button>

              <div className={`section-body ${showAdvanced ? 'open' : ''}`} aria-hidden={!showAdvanced}>
                <div className="upload-form-grid">
                    <div className="form-group span-2">
                      <label htmlFor="videoName">
                        视频名称
                        <HelpCircle
                          size={18}
                          strokeWidth={1.5}
                          className="help-icon help-icon--gap"
                          onMouseDown={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); }}
                          onClick={() => { blurActiveInput(); setShowVideoNameHelpDialog(true); }}
                          aria-label="帮助信息"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e: React.KeyboardEvent<SVGSVGElement>) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setShowVideoNameHelpDialog(true);
                            }
                          }}
                        />
                      </label>
                      <Input
                        type="text"
                        id="videoName"
                        value={videoName}
                        onChange={(e) => {
                          const v = (e.target as HTMLInputElement).value;
                          setVideoName(v);
                          if (v.trim().length > 0) {
                            setTopNotice(null);
                          } else {
                            setTopNotice('请填写视频名称');
                          }
                        }}
                        placeholder="例如：我的第一个视频"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="visibility">
                        视频可见性
                        <HelpCircle
                          size={18}
                          strokeWidth={1.5}
                          className="help-icon help-icon--gap"
                          onMouseDown={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); }}
                          onClick={() => { blurActiveInput(); setShowVisibilityHelpDialog(true); }}
                          aria-label="帮助信息"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e: React.KeyboardEvent<SVGSVGElement>) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setShowVisibilityHelpDialog(true);
                            }
                          }}
                        />
                      </label>
                      <Select
                        value={visibility}
                        onValueChange={(value) => setVisibility(value as 'private' | 'public')}
                        disabled={userRole === 'premium'} // 高级用户只能选择 private
                      >
                        <SelectItem value="private">私有（仅本人可见）</SelectItem>
                        {(userRole === 'super' || userRole === 'admin') && (
                          <SelectItem value="public">公开（所有人可见）</SelectItem>
                        )}
                      </Select>
                      {userRole === 'premium' && (
                        <small className="form-help-text">
                          高级用户只能上传私有视频
                        </small>
                      )}
                      {userRole === 'admin' && (
                        <small className="form-help-text">
                          管理者可以上传公开或私有视频，后续可在用户管理中修改
                        </small>
                      )}
                    </div>
                  </div>

                  {userRole === 'admin' && (
                    <div className="advanced-toggles">
                      <div className="toggle-row">
                        <label htmlFor="enableWordAlignment" className="toggle-label" style={{ alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            id="enableWordAlignment"
                            checked={enableWordAlignment}
                            onChange={(e) => setEnableWordAlignment(e.target.checked)}
                          />
                          <span>启用词级对齐</span>
                          <HelpCircle
                            size={18}
                            strokeWidth={1.5}
                            className="help-icon help-icon--gap"
                            onMouseDown={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={() => { blurActiveInput(); setShowWordAlignHelpDialog(true); }}
                            aria-label="帮助信息"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e: React.KeyboardEvent<SVGSVGElement>) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowWordAlignHelpDialog(true);
                              }
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
            </div>

            <div className="settings-section">
              <button
                type="button"
                className="section-toggle"
                onClick={() => setShowStatusSection(!showStatusSection)}
                aria-expanded={showStatusSection}
              >
                <span className="section-title">处理状态</span>
                <span className={`chevron ${showStatusSection ? 'open' : ''}`} aria-hidden="true">▸</span>
              </button>

              <div className={`section-body ${showStatusSection ? 'open' : ''}`} aria-hidden={!showStatusSection}>
                <div className={`status ${statusType}`} role="region" aria-live="polite">
                  <span className="status-text">{status}</span>
                </div>

                {completedVideoId && (
                  <div className="result" style={{ marginTop: 12 }}>
                    <p>视频处理已完成，可以开始播放了。</p>
                    <button
                      className="button result-button"
                      onClick={() =>
                        navigate(
                          `/player?video=${encodeURIComponent(completedVideoId)}&from=upload`
                        )
                      }
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-inline">
                        <polygon points="23 7 16 12 23 17 23 7"></polygon>
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                      </svg>
                      <span style={{ marginLeft: 8 }}>前往播放器</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
      </div>

      {/* 透明垫块，避免 upload-card 被 upload-sticky-bar 遮挡 */}
      <div className="upload-card-spacer"></div>

      {/* 底部 sticky action bar（移到外层，直接接触 app-shell-body） */}
      <div className="upload-sticky-bar">
        {(() => {
          const isProcessing = showProgress && !completedVideoId;
          const isCompleted = !!completedVideoId;
          const hasError = statusType === 'error' && !showProgress && !completedVideoId;
          const canProcess = selectedFile && !isProcessing && !isCompleted && remainingUploads > 0 && videoName.trim().length > 0;

          let buttonText = '🚀 开始处理';
          let iconNode = <Play size={16} className="icon-inline" aria-hidden="true" />;
          if (isCompleted) {
            iconNode = <CheckCircle size={16} className="icon-inline" aria-hidden="true" />;
            buttonText = '处理完成';
          } else if (isProcessing) {
            iconNode = <Clock size={16} className="icon-inline" aria-hidden="true" />;
            buttonText = '处理中...';
          } else if (hasError) {
            iconNode = <RefreshCw size={16} className="icon-inline" aria-hidden="true" />;
            buttonText = '重试';
          } else {
            buttonText = '开始处理';
          }

          return (
            <button
              className="button sticky-button"
              onClick={handleProcess}
              disabled={!canProcess}
            >
              {iconNode}
              <span style={{ marginLeft: 8 }}>{buttonText}</span>
            </button>
          );
        })()}
        {/* result moved into the collapsible '处理状态' section */}
      </div>
    </AppShell>
  );
};

export default VideoUpload;

